/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Logger }            from "@/util/logger";
import { Scenario }          from "@/models/scenario/scenario";

import {
  BufferAttribute,
  BufferGeometry,
  Color,
  LineBasicMaterial,
  LineSegments,
} from 'three';

/**
 *
 * @class UserLoggingBuilder
 *
 * this builder renders the boundaries for user logging data capture
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class UserLoggingBuilder {

  /** where the X data points start */
  private            dataX: number             = 0;

  /** where the Y data points start */
  private            dataY: number             = 0;

  /** the unscaled max time value */
  private        dataWidth: number             = 0;

  /** the unscaled max distance value */
  private       dataHeight: number             = 0;

  /** are we in dark or light mode? */
  private      nightMode: boolean        = true;

  /** the (maybe timezone specific) scenario */
  private         scenario: Scenario|null       = null;

  /** standard constructor */
  constructor(init?: Partial<UserLoggingBuilder>) {

    Object.assign(this, init);
  }

  /** force the data start (horizontal) */
  public setX(x: number): UserLoggingBuilder {

    this.dataX = x;

    return this;
  }

  /** fetch the data start (horizontal) */
  public getX(): number {

    return this.dataX;
  }

  /** force the data start (vertical) */
  public setY(y: number): UserLoggingBuilder {

    this.dataY = y;

    return this;
  }

  /** fetch the data start (vertical) */
  public getY(): number {

    return this.dataY;
  }

  /** force the data end (horizontal) */
  public setWidth(width: number): UserLoggingBuilder {

    this.dataWidth = width;

    return this;
  }

  /** fetch the data end (horizontal) */
  public getWidth(): number {

    return this.dataWidth;
  }

  /** force the data end (vertical) */
  public setHeight(height: number): UserLoggingBuilder {

    this.dataHeight = height;

    return this;
  }

  /** fetch the data end (vertical) */
  public getHeight(): number {

    return this.dataHeight;
  }

  /** force night mode  */
  public setNightMode(mode: boolean): UserLoggingBuilder {

    this.nightMode = mode;

    return this;
  }

  /** fetch night mode */
  public getNightMode(): boolean {

    return this.nightMode;
  }

  /** force the scenario */
  public setScenario(scenario: Scenario): UserLoggingBuilder {

    this.scenario = scenario;

    return this;
  }

  /** fetch the scenario */
  public getScenario(): Scenario {

    return this.scenario;
  }

  /** build the set of line segments as a complete geometry/mesh */
  public build(): LineSegments|null {

    let b: UserLoggingBuilder = this;

    if (!b.scenario) {

      Logger.error(`[UserLoggingBuilder] build - no scenario. Aborting.`);
      return null;
    }

    /* setup the user logging time range marker lines */

    let startHour: number  = this.scenario.traceStartSeconds() / 3600.0;
    let endHour:   number  = this.scenario.traceEndSeconds() / 3600.0;

    const lineColor: Color = new Color('#FF0000');

    if (!this.nightMode) {

      lineColor.set('#000000');
    }

    let material: LineBasicMaterial = new LineBasicMaterial( {
      color: 0xff0000
    });

    let geometry: BufferGeometry = new BufferGeometry();
    let positions: Float32Array  = new Float32Array((2 * 3) + (2 * 3));
    let indices: number[]        = [];
    let vIndex: number           = 0;
    let pIndex: number           = 0;

    geometry.name                = 'userlog-window';

    /* draw the vertical line that is the start of the user logging window */

    {
      const sx: number      = startHour;
      const sy: number      = 0;
      const ex: number      = startHour;
      const ey: number      = this.dataHeight;

      positions[vIndex]     = sx;
      positions[vIndex + 1] = sy;
      positions[vIndex + 2] = -0.002;

      positions[vIndex + 3] = ex;
      positions[vIndex + 4] = ey;
      positions[vIndex + 5] = -0.002;

      indices.push(pIndex);
      indices.push(pIndex + 1);

      vIndex += 6;
      pIndex += 2;
    }

    /* draw the horizontal line that is the end of the user logging window */

    {
      const sx: number      = endHour;
      const sy: number      = 0;
      const ex: number      = endHour;
      const ey: number      = this.dataHeight;

      positions[vIndex]     = sx;
      positions[vIndex + 1] = sy;
      positions[vIndex + 2] = -0.002;

      positions[vIndex + 3] = ex;
      positions[vIndex + 4] = ey;
      positions[vIndex + 5] = -0.002;

      indices.push(pIndex);
      indices.push(pIndex + 1);

    }

    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));

    /* all done */

    return new LineSegments(geometry, material);
  }

}
